import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Copy,
  Eye,
  EyeOff,
  Minus,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { Portal } from '../components/ui.jsx';
import {
  DAY_END,
  DAY_START,
  DEFAULT_SLOT_MINUTES,
  DRAG_SNAP_MINUTES,
  MIN_ENTRY_PX,
  SLOT_MINUTE_OPTIONS,
  SLOT_PX,
  clamp,
} from '../lib/calendar.js';
import {
  addDays,
  byWeek,
  entryHours,
  fmtDur,
  fmtHM,
  fmtTime,
  isoDate,
  mondayOf,
} from '../lib/time.js';
import { api } from '../lib/api.js';
import { CalendarEntry } from '../components/CalendarEntry.jsx';
import { CalendarGhost } from '../components/CalendarGhost.jsx';
import { CalendarEventBlock } from '../components/CalendarEventBlock.jsx';
import { ProjectSuggestions } from '../components/ProjectSuggestions.jsx';
import { timerBlocks } from '../lib/timer.js';
import { serverNow } from '../lib/serverTimer.js';
import {
  FUTURE_WORK_MESSAGE,
  isFutureWork,
  orgToday,
} from '../lib/futureWork.js';
import { sumByWorkType } from '../lib/workTypes.js';
import { useHolidays } from '../lib/useHolidays.js';

function layoutEntries(entries) {
  if (!entries.length) return new Map();
  const sorted = [...entries].sort((a, b) => a.start - b.start);
  const result = new Map();
  let i = 0;
  while (i < sorted.length) {
    let maxEnd = sorted[i].end;
    let j = i + 1;
    while (j < sorted.length && sorted[j].start < maxEnd) {
      maxEnd = Math.max(maxEnd, sorted[j].end);
      j++;
    }
    const group = sorted.slice(i, j);
    const colEnds = [];
    for (const entry of group) {
      let col = colEnds.findIndex((end) => end <= entry.start);
      if (col === -1) col = colEnds.length;
      colEnds[col] = entry.end;
      result.set(entry.key, { col, cols: 0 });
    }
    const totalCols = colEnds.length;
    for (const entry of group) {
      result.set(entry.key, { ...result.get(entry.key), cols: totalCols });
    }
    i = j;
  }
  return result;
}

// Pixels the cursor must travel before a create gesture counts as a drag
// (sizing the entry) rather than a click (default 1-hour entry).
const CREATE_DRAG_PX = 4;

// Height of the sticky day-header row (grid-template-rows first track).
const CAL_HEADER_PX = 58;

// Day-column index (0-6) currently under the given client X, or null.
function dayColumnAtX(clientX) {
  for (const col of document.querySelectorAll('.day-column')) {
    const rect = col.getBoundingClientRect();
    if (clientX >= rect.left && clientX < rect.right) {
      return Number(col.dataset.day);
    }
  }
  return null;
}

export function CalendarView({
  data,
  reload,
  timer,
  openEntry,
  openQuickAdd,
  onEntryChange,
  onEntryDelete,
  onOpenSettings,
  weekOffset,
  setWeekOffset,
  outlook,
  onStartTimerFromEvent,
}) {
  const [drag, setDragState] = useState(null);
  const [savingEntry, setSavingEntry] = useState(false);
  const [entryError, setEntryError] = useState(null);
  const [menu, setMenu] = useState(null);
  const [, setNowTick] = useState(0);
  const [slotMinutes, setSlotMinutes] = useState(() => {
    const stored = Number(
      window.localStorage.getItem('tt_calendar_slot_minutes'),
    );
    return SLOT_MINUTE_OPTIONS.includes(stored) ? stored : DEFAULT_SLOT_MINUTES;
  });
  const [showOutlookEvents, setShowOutlookEvents] = useState(
    () => window.localStorage.getItem('tt_calendar_show_outlook') !== '0',
  );
  const [outlookHintDismissed, setOutlookHintDismissed] = useState(
    () => data.outlookHintDismissed,
  );
  const dragRef = useRef(null);
  const gridRef = useRef(null);
  const suppressClick = useRef(false);
  const scrollAnchorRef = useRef(null);

  // Each slot row is SLOT_PX tall, so the hour height (used for entry
  // positioning) follows from the chosen slot size.
  const hourHeight = (SLOT_PX * 60) / slotMinutes;
  const slotsPerDay = ((DAY_END - DAY_START) * 60) / slotMinutes;
  const slotIndex = SLOT_MINUTE_OPTIONS.indexOf(slotMinutes);

  // step -1 => coarser (towards 1h), +1 => finer (towards 5 min)
  function changeSlot(step) {
    const next = SLOT_MINUTE_OPTIONS[slotIndex + step];
    if (!next) return;
    const grid = gridRef.current;
    if (grid) {
      // Remember the time at the viewport's vertical centre so the same spot
      // stays put after the scale changes (instead of the scroll jumping).
      const centerOffset = grid.clientHeight / 2 - CAL_HEADER_PX;
      scrollAnchorRef.current =
        DAY_START + (grid.scrollTop + centerOffset) / hourHeight;
    }
    setSlotMinutes(next);
  }

  // Snap an hour value to the current slot grid.
  function snapHour(hour) {
    const perHour = 60 / slotMinutes;
    return Math.round(hour * perHour) / perHour;
  }

  // Snap an hour value to the fixed drag grid (5 min) used by move/resize.
  function snapDrag(hour) {
    const perHour = 60 / DRAG_SNAP_MINUTES;
    return Math.round(hour * perHour) / perHour;
  }

  useEffect(() => {
    window.localStorage.setItem(
      'tt_calendar_slot_minutes',
      String(slotMinutes),
    );
  }, [slotMinutes]);

  useEffect(() => {
    window.localStorage.setItem(
      'tt_calendar_show_outlook',
      showOutlookEvents ? '1' : '0',
    );
  }, [showOutlookEvents]);

  // Keep dragRef always in sync so event handlers never read stale state
  function setDrag(next) {
    dragRef.current = next;
    setDragState(next);
  }

  // Optimistic + permanent: hide right away, persist server-side in the
  // background, then refresh the shared bootstrap data so a remount (e.g.
  // switching views and back) re-seeds from the now-dismissed value instead
  // of the stale one already in `data`.
  function dismissOutlookHint() {
    setOutlookHintDismissed(true);
    api('/me/dismiss-outlook-hint', { method: 'POST' })
      .then(() => reload())
      .catch(() => {});
  }
  const today = new Date();
  const todayKey = isoDate(today);
  const nowHours = today.getHours() + today.getMinutes() / 60;
  const monday = addDays(mondayOf(today), weekOffset * 7);
  const weekDates = Array.from({ length: 7 }, (_, index) =>
    addDays(monday, index),
  );
  const weekEntries = byWeek(data.entries, monday);
  const holidays = useHolidays(
    data.me.id,
    isoDate(monday),
    isoDate(weekDates[6]),
  );
  const { events, status } = outlook;
  const canConnectOutlook =
    data.outlookConfigured &&
    !data.outlookConnected &&
    !data.outlookNeedsReconnect &&
    !outlookHintDismissed;
  // The week's total is work plus paid absence, unpaid absence shown apart;
  // a day's header below still counts everything logged on it, so a day off
  // reads as filled.
  const { total, unpaidAbsence } = sumByWorkType(weekEntries, [
    ...data.projects,
    ...data.allProjects,
  ]);
  const dailyTotals = weekEntries.reduce((acc, entry) => {
    acc[entry.workDate] = (acc[entry.workDate] || 0) + entryHours(entry);
    return acc;
  }, {});

  // Preview a live timer as provisional "to-be" blocks — one per work slice,
  // exactly what would be saved. Shown whenever a timer is active (running,
  // paused, or awaiting the save confirmation — `timer` is null only when
  // idle), so the preview stays put while the Stop popover is open. The active
  // slice (last one, only while running) grows towards the now-line; paused and
  // pending-save slices sit settled. `today` is our clock (put on the
  // server's, like the timer's instants), so the blocks grow with the
  // per-minute re-render.
  const ghostBlocks = timer
    ? timerBlocks(timer.segments, timer.activeStart, serverNow(timer, today))
    : [];
  const ghostLiveIndex =
    timer?.status === 'running' ? ghostBlocks.length - 1 : -1;
  const ghostProject = timer
    ? data.projects.find((p) => p.id === timer.projectId) ||
      data.allProjects.find((p) => p.id === timer.projectId)
    : null;

  // Clicking a ghost isn't editing a real entry — bring attention back to the
  // timer bar (stop / edit start / change project) with a brief highlight.
  function focusTimer() {
    const bar = document.querySelector('.timer-bar');
    if (!bar) return;
    bar.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    bar.classList.remove('timer-bar-flash');
    void bar.offsetWidth; // restart the animation on repeat clicks
    bar.classList.add('timer-bar-flash');
    window.setTimeout(() => bar.classList.remove('timer-bar-flash'), 1100);
  }

  function rawHourFromY(event, element) {
    const rect = element.getBoundingClientRect();
    return DAY_START + (event.clientY - rect.top) / hourHeight;
  }

  function hourFromY(event, element) {
    return snapHour(rawHourFromY(event, element));
  }

  function startCreate(event, day, workDate) {
    if (
      event.button !== 0 ||
      event.target.closest('[data-calendar-entry], [data-calendar-event]')
    )
      return;
    const start = clamp(
      snapDrag(rawHourFromY(event, event.currentTarget)),
      DAY_START,
      DAY_END - DRAG_SNAP_MINUTES / 60,
    );
    setDrag({
      kind: 'create',
      day,
      workDate,
      anchorStart: start,
      anchorY: event.clientY,
      start,
      end: Math.min(DAY_END, start + 1),
      moved: false,
    });
    event.preventDefault();
  }

  function startMove(event, entry) {
    if (event.button !== 0) return;
    const column = event.currentTarget.closest('.day-column');
    setDrag({
      kind: 'move',
      entry,
      day: Number(column.dataset.day),
      originDay: Number(column.dataset.day),
      anchorHour: rawHourFromY(event, column),
      start: entry.start,
      end: entry.end,
      moved: false,
    });
    event.preventDefault();
    event.stopPropagation();
  }

  function startResize(event, entry) {
    if (event.button !== 0) return;
    const column = event.currentTarget.closest('.day-column');
    setDrag({
      kind: 'resize',
      entry,
      day: Number(column.dataset.day),
      start: entry.start,
      end: entry.end,
      moved: false,
    });
    event.preventDefault();
    event.stopPropagation();
  }

  function startResizeTop(event, entry) {
    if (event.button !== 0) return;
    const column = event.currentTarget.closest('.day-column');
    setDrag({
      kind: 'resize-start',
      entry,
      day: Number(column.dataset.day),
      start: entry.start,
      end: entry.end,
      moved: false,
    });
    event.preventDefault();
    event.stopPropagation();
  }

  // Drop, move, resize and duplicate save without a dialog, so a refusal
  // (work on a future day, or anything the server says no to) is shown here
  // and the entry stays where it was.
  async function saveFromCalendar(entry) {
    const project = data.projects.find((p) => p.id === entry.projectId);
    if (isFutureWork(project, entry.workDate, orgToday(data.orgTimezone))) {
      setEntryError(FUTURE_WORK_MESSAGE);
      return;
    }
    setEntryError(null);
    try {
      await onEntryChange(entry);
    } catch (err) {
      setEntryError(err.message || 'Could not save. Please try again.');
    }
  }

  async function projectDrop(event, workDate) {
    event.preventDefault();
    const projectId =
      event.dataTransfer.getData('application/project-id') ||
      event.dataTransfer.getData('text/plain');
    if (!projectId) return;
    const start = clamp(
      hourFromY(event, event.currentTarget),
      DAY_START,
      DAY_END - 1,
    );
    setSavingEntry(true);
    try {
      await saveFromCalendar({
        projectId,
        workDate,
        start,
        end: start + 1,
        description: '',
      });
    } finally {
      setSavingEntry(false);
    }
  }

  async function duplicateEntry(entry) {
    setSavingEntry(true);
    try {
      await saveFromCalendar({
        projectId: entry.projectId,
        workDate: entry.workDate,
        start: entry.start,
        end: entry.end,
        description: entry.description,
      });
    } finally {
      setSavingEntry(false);
    }
  }

  function openContextMenu(event, entry) {
    setMenu({ entry, x: event.clientX, y: event.clientY });
  }

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    grid.scrollTop = 6 * hourHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-render every minute so the current-time line keeps up.
  useEffect(() => {
    const id = window.setInterval(() => setNowTick((t) => t + 1), 60000);
    return () => window.clearInterval(id);
  }, []);

  // After a zoom changes the scale, restore the anchored centre time so the
  // view stays put relative to the entries.
  useLayoutEffect(() => {
    const anchor = scrollAnchorRef.current;
    if (anchor == null) return;
    scrollAnchorRef.current = null;
    const grid = gridRef.current;
    if (!grid) return;
    const centerOffset = grid.clientHeight / 2 - CAL_HEADER_PX;
    grid.scrollTop = (anchor - DAY_START) * hourHeight - centerOffset;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotMinutes]);

  useEffect(() => {
    if (!menu) return undefined;
    function onKey(event) {
      if (event.key === 'Escape') setMenu(null);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menu]);

  // Register listeners only once when a drag starts; read all mutable state from
  // dragRef so handlers are never stale even if React hasn't re-rendered yet.
  const isDragging = drag !== null;
  useEffect(() => {
    if (!isDragging) return undefined;

    function onMouseMove(event) {
      const d = dragRef.current;
      if (!d || d.kind === 'saving') return;
      const column = document.querySelector(`[data-day="${d.day}"]`);
      if (!column) return;
      const rawHour = clamp(rawHourFromY(event, column), DAY_START, DAY_END);
      if (d.kind === 'create') {
        // Stay a click (1-hour default) until the cursor actually moves a few
        // pixels; then size the entry by dragging, snapped to the slot grid.
        if (!d.moved && Math.abs(event.clientY - d.anchorY) <= CREATE_DRAG_PX) {
          return;
        }
        const hour = snapDrag(rawHour);
        const start = Math.min(d.anchorStart, hour);
        const end = Math.max(d.anchorStart + DRAG_SNAP_MINUTES / 60, hour);
        setDrag({ ...d, start, end, moved: true });
      }
      if (d.kind === 'move') {
        // Move in fixed 5-min steps regardless of the slot size, and follow
        // the cursor across day columns so entries can change day.
        const delta = snapDrag(rawHour - d.anchorHour);
        const duration = d.entry.end - d.entry.start;
        const start = clamp(
          d.entry.start + delta,
          DAY_START,
          DAY_END - duration,
        );
        const targetDay = dayColumnAtX(event.clientX);
        const day = targetDay == null ? d.day : targetDay;
        setDrag({
          ...d,
          day,
          start,
          end: start + duration,
          moved: d.moved || delta !== 0 || day !== d.originDay,
        });
      }
      if (d.kind === 'resize') {
        // Resize snaps the end to the fixed 5-min grid.
        const minEnd = d.start + DRAG_SNAP_MINUTES / 60;
        setDrag({
          ...d,
          end: Math.max(minEnd, snapDrag(rawHour)),
          moved: true,
        });
      }
      if (d.kind === 'resize-start') {
        // Resize from the top snaps the start to the fixed 5-min grid.
        const maxStart = d.end - DRAG_SNAP_MINUTES / 60;
        setDrag({
          ...d,
          start: clamp(snapDrag(rawHour), DAY_START, maxStart),
          moved: true,
        });
      }
    }

    async function onMouseUp() {
      const d = dragRef.current;
      if (!d) return;
      if (d.kind === 'create') {
        // Click or drag: d.start/d.end already hold a 1-hour default for a
        // click and the dragged range for a drag.
        openQuickAdd({ workDate: d.workDate, start: d.start, end: d.end });
      }
      if (
        (d.kind === 'move' ||
          d.kind === 'resize' ||
          d.kind === 'resize-start') &&
        d.moved
      ) {
        suppressClick.current = true;
        setSavingEntry(true);
        setDrag({ ...d, kind: 'saving' });
        try {
          await saveFromCalendar({
            ...d.entry,
            workDate: isoDate(weekDates[d.day]),
            start: d.start,
            end: d.end,
          });
        } finally {
          setSavingEntry(false);
          setDrag(null);
          window.setTimeout(() => {
            suppressClick.current = false;
          }, 0);
        }
      } else {
        setDrag(null);
      }
    }

    function onBlur() {
      if (dragRef.current?.kind !== 'saving') setDrag(null);
    }

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('blur', onBlur);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging]);

  return (
    <div className="calendar-view">
      <div className="page-head compact">
        <div>
          <h1>My week</h1>
          <p>
            {isoDate(monday)} - {isoDate(addDays(monday, 6))}
          </p>
        </div>
        <div className="calendar-head-actions">
          <div
            className="segmented calendar-zoom"
            role="group"
            aria-label="Time slot size"
          >
            <button
              type="button"
              onClick={() => changeSlot(-1)}
              disabled={slotIndex <= 0}
              data-tooltip="Larger time slots"
              aria-label="Larger time slots"
            >
              <Minus size={14} />
            </button>
            <button
              type="button"
              onClick={() => changeSlot(1)}
              disabled={slotIndex >= SLOT_MINUTE_OPTIONS.length - 1}
              data-tooltip="Smaller time slots"
              aria-label="Smaller time slots"
            >
              <Plus size={14} />
            </button>
          </div>
          {data.outlookConnected && (
            <button
              type="button"
              className={`outlook-visibility-toggle${
                showOutlookEvents ? '' : ' off'
              }`}
              onClick={() => setShowOutlookEvents((prev) => !prev)}
              aria-label={
                showOutlookEvents
                  ? 'Hide Outlook events'
                  : 'Show Outlook events'
              }
              data-tooltip={
                showOutlookEvents
                  ? 'Hide Outlook events'
                  : 'Show Outlook events'
              }
              aria-pressed={showOutlookEvents}
            >
              {showOutlookEvents ? <Eye size={14} /> : <EyeOff size={14} />}
            </button>
          )}
          <div className="segmented calendar-stepper" aria-label="Change week">
            <button
              onClick={() => setWeekOffset(weekOffset - 1)}
              data-tooltip="Previous week"
              aria-label="Previous week"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => setWeekOffset(weekOffset + 1)}
              data-tooltip="Next week"
              aria-label="Next week"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <strong className="total-pill calendar-total">
            {fmtDur(total)} · {(total / 8).toFixed(2)} MD
            {unpaidAbsence > 0 &&
              ` · ${fmtDur(unpaidAbsence)} unpaid absence not counted`}
          </strong>
          <button
            className="primary"
            onClick={() => openQuickAdd({ workDate: isoDate(today) })}
          >
            <Plus size={14} /> Log time
          </button>
        </div>
      </div>

      {canConnectOutlook && (
        <div className="calendar-connect-hint">
          <CalendarDays size={16} />
          <span>See your planned Outlook events beside tracked time.</span>
          <button
            type="button"
            className="secondary small"
            onClick={onOpenSettings}
          >
            Connect Outlook
          </button>
          <button
            type="button"
            className="icon-button"
            aria-label="Dismiss"
            onClick={dismissOutlookHint}
          >
            <X size={14} />
          </button>
        </div>
      )}
      {(status === 'needs_reconnect' || data.outlookNeedsReconnect) && (
        <div className="calendar-connect-hint warning">
          <CalendarDays size={16} />
          <span>Your Outlook connection needs to be reconnected.</span>
          <button
            type="button"
            className="secondary small"
            onClick={onOpenSettings}
          >
            Open Settings
          </button>
        </div>
      )}
      {entryError && (
        <div className="error calendar-entry-error" role="alert">
          <span>{entryError}</span>
          <button
            type="button"
            className="icon-button"
            aria-label="Dismiss"
            onClick={() => setEntryError(null)}
          >
            <X size={20} />
          </button>
        </div>
      )}
      <div className="calendar-layout">
        {savingEntry && (
          <div className="calendar-saving">Saving time entry...</div>
        )}
        <div
          className="calendar-grid"
          ref={gridRef}
          style={{
            '--cal-body-height': `${(DAY_END - DAY_START) * hourHeight}px`,
          }}
        >
          <div className="time-axis" />
          {weekDates.map((date, day) => {
            const dateKey = isoDate(date);
            const isToday = dateKey === isoDate(today);
            const holiday = holidays.get(dateKey);
            return (
              <div
                key={day}
                className={`day-head ${day >= 5 || holiday ? 'weekend' : ''} ${
                  isToday ? 'today' : ''
                }`}
                style={{ gridColumn: day + 2 }}
                data-tooltip={holiday}
              >
                <strong>
                  {date.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                  })}
                </strong>
                <span className="day-total">
                  {fmtHM(dailyTotals[dateKey] || 0)}
                </span>
                {holiday && <span className="day-holiday">{holiday}</span>}
              </div>
            );
          })}
          <div className="time-column">
            {Array.from({ length: slotsPerDay }, (_, index) => (
              <div
                key={`h${index}`}
                className="time-label"
                style={{ top: index * SLOT_PX - 7 }}
              >
                {fmtTime(DAY_START + (index * slotMinutes) / 60)}
              </div>
            ))}
          </div>
          {weekDates.map((date, day) => {
            const workDate = isoDate(date);
            // A public holiday of the user's country reads as a weekend.
            const isWeekend = day >= 5 || holidays.has(workDate);
            const movingEntry = drag?.kind === 'move' ? drag.entry : null;
            let dayEntries = weekEntries.filter(
              (entry) => entry.workDate === workDate,
            );
            // Only relocate the dragged entry when it's actually changing day.
            // For a same-day move, leaving it in place keeps its overlap column
            // stable (otherwise re-adding it reshuffles side-by-side columns).
            if (movingEntry && drag.day !== drag.originDay) {
              dayEntries = dayEntries.filter(
                (entry) => entry.id !== movingEntry.id,
              );
              if (drag.day === day) dayEntries = [...dayEntries, movingEntry];
            }
            const dayEvents =
              showOutlookEvents && data.outlookConnected
                ? events
                    .filter((event) => event.workDate === workDate)
                    .map((event, index) => ({
                      ...event,
                      key: `event:${workDate}:${index}`,
                    }))
                : [];
            const layout = layoutEntries([
              ...dayEntries.map((entry) => ({
                ...entry,
                key: `entry:${entry.id}`,
              })),
              ...dayEvents,
            ]);
            return (
              <div
                key={day}
                className={`day-column ${isWeekend ? 'weekend' : ''}`}
                data-day={day}
                style={{ gridColumn: day + 2 }}
                onMouseDown={(event) => startCreate(event, day, workDate)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => projectDrop(event, workDate)}
              >
                {!isWeekend && (
                  <>
                    <div
                      className="non-work-block"
                      style={{ top: 0, height: (8 - DAY_START) * hourHeight }}
                    />
                    <div
                      className="non-work-block"
                      style={{
                        top: (18 - DAY_START) * hourHeight,
                        height: (DAY_END - 18) * hourHeight,
                      }}
                    />
                  </>
                )}
                {Array.from({ length: slotsPerDay + 1 }, (_, index) => (
                  <div
                    key={index}
                    className="hour-line"
                    style={{ top: index * SLOT_PX }}
                  />
                ))}
                {dayEntries.map((entry) => {
                  const { col = 0, cols = 1 } =
                    layout.get(`entry:${entry.id}`) || {};
                  return (
                    <CalendarEntry
                      key={entry.id}
                      entry={entry}
                      project={
                        data.projects.find((p) => p.id === entry.projectId) ||
                        data.allProjects.find((p) => p.id === entry.projectId)
                      }
                      drag={drag}
                      col={col}
                      cols={cols}
                      hourHeight={hourHeight}
                      onOpen={() => {
                        if (!suppressClick.current) openEntry(entry);
                      }}
                      onMoveStart={startMove}
                      onResizeStart={startResize}
                      onResizeStartTop={startResizeTop}
                      onContextMenu={openContextMenu}
                    />
                  );
                })}
                {dayEvents.map((event) => {
                  const { col = 0, cols = 1 } = layout.get(event.key) || {};
                  return (
                    <CalendarEventBlock
                      key={event.key}
                      event={event}
                      col={col}
                      cols={cols}
                      hourHeight={hourHeight}
                      onOpen={() =>
                        openQuickAdd({
                          workDate: event.workDate,
                          start: event.start,
                          end: event.end,
                          description: event.subject,
                          projectId: '',
                        })
                      }
                      onStartTimer={() => onStartTimerFromEvent(event.subject)}
                      timerActive={Boolean(timer)}
                    />
                  );
                })}
                {drag?.kind === 'create' && drag.day === day && (
                  <div
                    className="cal-draft"
                    style={{
                      top: (drag.start - DAY_START) * hourHeight,
                      height: Math.max(
                        MIN_ENTRY_PX,
                        (drag.end - drag.start) * hourHeight,
                      ),
                    }}
                  >
                    New entry · {fmtDur(drag.end - drag.start)}
                    <small>
                      {fmtTime(drag.start)}-{fmtTime(drag.end)}
                    </small>
                  </div>
                )}
                {ghostBlocks.map((block, index) =>
                  block.workDate === workDate ? (
                    <CalendarGhost
                      key={`ghost-${index}`}
                      block={block}
                      project={ghostProject}
                      live={index === ghostLiveIndex}
                      hourHeight={hourHeight}
                      onFocusTimer={focusTimer}
                    />
                  ) : null,
                )}
                {workDate === todayKey && (
                  <div
                    className="now-line"
                    style={{ top: (nowHours - DAY_START) * hourHeight }}
                  />
                )}
              </div>
            );
          })}
        </div>
        <ProjectSuggestions
          projects={data.projects}
          projectLastUsed={data.projectLastUsed}
          weekEntries={weekEntries}
        />
      </div>
      {menu && (
        <Portal>
          <div
            className="context-menu-backdrop"
            onMouseDown={() => setMenu(null)}
            onContextMenu={(event) => {
              event.preventDefault();
              setMenu(null);
            }}
          >
            <div
              className="context-menu"
              style={{ top: menu.y, left: menu.x }}
              onMouseDown={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => {
                  duplicateEntry(menu.entry);
                  setMenu(null);
                }}
              >
                <Copy size={14} /> Duplicate
              </button>
              <button
                type="button"
                className="cm-danger"
                onClick={() => {
                  onEntryDelete(menu.entry.id);
                  setMenu(null);
                }}
              >
                <Trash2 size={14} /> Delete
              </button>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
}
